// @ts-check

import { useCallback } from "react";
import { findIndex, isString } from "lodash";
import { ENTITY_TYPES as ENTITY_TYPE_CONSTANTS } from "src/consts/entityTypes";
import Consts from "../consts";
import { useUserPreferences } from "./useUserPreferences";

/** @type {user_prefs.PinnedItemEntityType[]} */
const ENTITY_TYPES = [
  /** @type {user_prefs.PinnedItemEntityType} */ (ENTITY_TYPE_CONSTANTS.TEAM),
  /** @type {user_prefs.PinnedItemEntityType} */ (ENTITY_TYPE_CONSTANTS.PERSON),
  /** @type {user_prefs.PinnedItemEntityType} */ (ENTITY_TYPE_CONSTANTS.TAG),
];

/**
 * @param {user_prefs.PinnedItem} item
 * @returns {boolean}
 */
const validateItem = (item) => {
  return (
    isString(item.id) && Object.values(ENTITY_TYPES).includes(item.entityType)
  );
};

/**
 * Custom hook for managing pinned items in user preferences.
 */
export const useUserPinnedItems = () => {
  const {
    mergedPreferences,
    workspaceUserPreferences,
    updatePreferencesForWorkspaceUser,
  } = useUserPreferences();

  const updatePinnedItems = useCallback(
    /**
     * @param {user_prefs.PinnedItems} newPinnedItems
     * @returns {Promise<boolean>}
     */
    async (newPinnedItems) => {
      for (const item of newPinnedItems) {
        if (!validateItem(item)) {
          return false;
        }
      }

      return updatePreferencesForWorkspaceUser({
        ...workspaceUserPreferences,
        pinnedItems: newPinnedItems,
      });
    },
    [workspaceUserPreferences, updatePreferencesForWorkspaceUser]
  );

  const addPinnedItem = useCallback(
    /**
     * @param {user_prefs.PinnedItem} item
     * @returns {Promise<boolean>}
     */
    async (item) => {
      const pinnedItems = mergedPreferences?.pinnedItems || [];

      const newPinnedItems = [...pinnedItems, item];

      return updatePinnedItems(newPinnedItems);
    },
    [mergedPreferences?.pinnedItems, updatePinnedItems]
  );

  const removePinnedItem = useCallback(
    /**
     * @param {user_prefs.PinnedItem} item
     * @returns {Promise<boolean>}
     */
    async (item) => {
      const pinnedItems = mergedPreferences?.pinnedItems || [];

      const removeIndex = findIndex(
        pinnedItems,
        (x) => x.entityType === item.entityType && x.id === item.id
      );

      if (removeIndex === -1) {
        return false;
      }

      const newPinnedItems = [...pinnedItems];
      newPinnedItems.splice(removeIndex, 1);

      return updatePinnedItems(newPinnedItems);
    },
    [mergedPreferences?.pinnedItems, updatePinnedItems]
  );

  const togglePinnedItem = useCallback(
    /**
     * @param {user_prefs.PinnedItem} item
     * @returns {Promise<boolean>}
     */
    async (item) => {
      const pinnedItems = mergedPreferences?.pinnedItems || [];

      const removeIndex = findIndex(
        pinnedItems,
        (x) => x.entityType === item.entityType && x.id === item.id
      );

      if (removeIndex === -1) {
        return addPinnedItem(item);
      }
      return removePinnedItem(item);
    },
    [addPinnedItem, mergedPreferences?.pinnedItems, removePinnedItem]
  );

  return {
    pinnedItems:
      mergedPreferences?.pinnedItems || Consts.defaultPreferences.pinnedItems,
    updatePinnedItems,
    addPinnedItem,
    removePinnedItem,
    togglePinnedItem,
  };
};
